import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, PlusCircle, History, BarChart3, ShieldCheck, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import OrderEntry from "../components/OrderEntry";
import OrderHistory from "../components/OrderHistory";
import TodayTotals from "../components/TodayTotals";
import EditHistory from "../components/EditHistory";
import PendingOrders from "../components/PendingOrders";
import { auth } from "../lib/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const user = auth.getUser();
  const isAdmin = (user?.title || "").trim().toLowerCase() === "admin";
  const [tab, setTab] = useState("new");
  const [editing, setEditing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleLogout = () => {
    auth.clear();
    navigate("/login", { replace: true });
  };

  const handleSaved = () => {
    setEditing(null);
    setRefreshKey((k) => k + 1);
  };

  const handleEdit = (order) => {
    setEditing(order);
    setTab("new");
  };

  return (
    <div className="min-h-screen bg-[#F5F2EB]">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-[#FFFCF5] border-b border-[#E6E2D8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png.jpg"
              alt="Beirut Bites Logo"
              className="h-10 w-10 rounded-lg object-contain"
            />
            <div className="leading-tight">
              <div className="font-heading text-lg font-black tracking-tight text-[#1A1918]">
                Beirut Bites
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5854]">
                Sales Console
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden sm:block text-right leading-tight">
                <div className="text-sm font-bold text-[#1A1918]" data-testid="logged-in-username">
                  {user.username}
                </div>
                <div className="text-xs text-[#5C5854]">{user.title || "Staff"}</div>
              </div>
            )}
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-button"
              className="border-[#E6E2D8] text-[#1A1918] hover:bg-[#F5F2EB] h-10"
            >
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12 flex flex-col gap-6">
        <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-6">
          <TabsList className="bg-[#FFFCF5] border border-[#E6E2D8] p-1 h-auto w-fit rounded-lg">
            <TabsTrigger
              value="new"
              data-testid="tab-new-order"
              className="data-[state=active]:bg-[#1A1918] data-[state=active]:text-white text-[#5C5854] font-bold px-4 py-2"
            >
              <PlusCircle size={14} className="mr-1.5" /> New Order
            </TabsTrigger>
            <TabsTrigger
              value="pending"
              data-testid="tab-pending-orders"
              className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-white text-[#5C5854] font-bold px-4 py-2"
            >
              <Clock size={14} className="mr-1.5" /> Pending Orders
            </TabsTrigger>
            <TabsTrigger
              value="history"
              data-testid="tab-history"
              className="data-[state=active]:bg-[#1A1918] data-[state=active]:text-white text-[#5C5854] font-bold px-4 py-2"
            >
              <History size={14} className="mr-1.5" /> History
            </TabsTrigger>
            <TabsTrigger
              value="totals"
              data-testid="tab-totals"
              className="data-[state=active]:bg-[#1A1918] data-[state=active]:text-white text-[#5C5854] font-bold px-4 py-2"
            >
              <BarChart3 size={14} className="mr-1.5" /> Daily Totals
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="edits"
                data-testid="tab-edit-history"
                className="data-[state=active]:bg-[#1A1918] data-[state=active]:text-white text-[#5C5854] font-bold px-4 py-2"
              >
                <ShieldCheck size={14} className="mr-1.5" /> Edit History
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="new" className="mt-0">
            <OrderEntry
              editing={editing}
              onSaved={handleSaved}
              onCancelEdit={() => setEditing(null)}
            />
          </TabsContent>
          <TabsContent value="pending" className="mt-0">
            <PendingOrders refreshKey={refreshKey} onEdit={handleEdit} />
          </TabsContent>
          <TabsContent value="history" className="mt-0">
            <OrderHistory refreshKey={refreshKey} onEdit={handleEdit} />
          </TabsContent>
          <TabsContent value="totals" className="mt-0">
            <TodayTotals refreshKey={refreshKey} />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="edits" className="mt-0">
              <EditHistory refreshKey={refreshKey} />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
